import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PreviewIcon from "@mui/icons-material/Preview";
import InfoIcon from "@mui/icons-material/Info";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import {
  obtenerInfoClienteEnvioManual,
  enviarCorreoManual,
} from "../../componentes/http/actions/CorreoAction";
import { obtenerClientesDisponiblesReenvio } from "../../componentes/http/actions/DocumentoAction";
import VistaPreviaEnvioManualModal from "../../componentes/modal/Correo/VistaPreviaEnvioManualModal";

const EnvioManualCorreo = () => {

  // Estados principales
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState("");
  const [infoCliente, setInfoCliente] = useState(null);
  const [mensajePersonalizado, setMensajePersonalizado] = useState("");
  const [enviarSinNormativas, setEnviarSinNormativas] = useState(false);

  // Estados de UI
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);
  const [mostrarDetallesNormativas, setMostrarDetallesNormativas] = useState(false);

  // Estado para vista previa
  const [mostrarVistaPrevia, setMostrarVistaPrevia] = useState(false);

  // Cargar clientes al montar el componente
  useEffect(() => {
    cargarClientes();
  }, []);

  // Cargar info del cliente cuando se selecciona uno
  useEffect(() => {
    if (clienteSeleccionado) {
      cargarInfoCliente(clienteSeleccionado);
    } else {
      setInfoCliente(null);
    }
  }, [clienteSeleccionado]);

  const cargarClientes = async () => {
    setLoadingClientes(true);
    try {
      const clientesData = await obtenerClientesDisponiblesReenvio(
        localStorage.getItem("token")
      );
      setClientes(clientesData);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      setError("Error al cargar la lista de clientes");
    } finally {
      setLoadingClientes(false);
    }
  };

  const cargarInfoCliente = async (clienteId) => {
    setLoadingInfo(true);
    setError("");
    try {
      const info = await obtenerInfoClienteEnvioManual(
        localStorage.getItem("token"),
        clienteId
      );
      setInfoCliente(info);
      
      // Resetear la opción de enviar sin normativas si el cliente no lo permite
      if (!info.puedeEnviarSinNormativas) {
        setEnviarSinNormativas(false);
      }
    } catch (error) {
      console.error("Error al cargar info del cliente:", error);
      setError("Error al cargar información del cliente");
      setInfoCliente(null);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleEnviar = async () => {
    if (!clienteSeleccionado) {
      setError("Debe seleccionar un cliente");
      return;
    }

    // Validar si no hay normativas y no está permitido enviar
    if (infoCliente?.cantidadNormativasNuevas === 0 && !infoCliente?.puedeEnviarSinNormativas) {
      setError("Este cliente no tiene normativas pendientes y no permite envío sin normativas (tipo Larrain Vial)");
      return;
    }

    setEnviando(true);
    setError("");
    setResultado(null);

    try {
      const data = {
        clienteId: parseInt(clienteSeleccionado),
        enviarSinNormativas: enviarSinNormativas,
        mensajePersonalizado: mensajePersonalizado || null,
      };

      const resultado = await enviarCorreoManual(
        localStorage.getItem("token"),
        data
      );

      setResultado(resultado);

    } catch (error) {
      console.error("Error al enviar:", error);
      setError(typeof error === 'string' ? error : "Error al enviar el correo");
    } finally {
      setEnviando(false);
    }
  };

  const handleReset = () => {
    setClienteSeleccionado("");
    setInfoCliente(null);
    setMensajePersonalizado("");
    setEnviarSinNormativas(false);
    setError("");
    setResultado(null);
  };

  const handleVerVistaPrevia = () => {
    setMostrarVistaPrevia(true);
  };

  const puedeEnviar = () => {
    if (!clienteSeleccionado || !infoCliente) return false;
    if (enviando) return false;
    if (resultado?.exitoso) return false;
    
    // Si no hay normativas
    if (infoCliente.cantidadNormativasNuevas === 0) {
      // Solo puede enviar si está permitido y está marcado el switch
      return infoCliente.puedeEnviarSinNormativas && enviarSinNormativas;
    }
    
    return true;
  };

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
        {/* Header */}
        <Grid item>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h4">
                  Envío Manual de Normativas
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider />
        </Grid>

        {/* Contenido Principal */}
        <Grid item>
          <Paper elevation={3} sx={{ p: 3 }}>
            {/* Selector de Cliente */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="cliente-select-label">Seleccionar Cliente</InputLabel>
              <Select
                labelId="cliente-select-label"
                value={clienteSeleccionado}
                onChange={(e) => setClienteSeleccionado(e.target.value)}
                disabled={loadingClientes || enviando}
                label="Seleccionar Cliente"
              >
                <MenuItem value="">
                  <em>-- Seleccione un cliente --</em>
                </MenuItem>
                {clientes.map((cliente) => (
                  <MenuItem key={cliente.id} value={cliente.id}>
                    {cliente.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Loading Info Cliente */}
            {loadingInfo && (
              <Box display="flex" justifyContent="center" my={4}>
                <CircularProgress />
              </Box>
            )}

            {/* Información del Cliente */}
            {infoCliente && !loadingInfo && (
              <Box sx={{ mb: 3 }}>
                <Paper elevation={2} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Información del Cliente
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">
                            Correos Configurados
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {infoCliente.correosConfigurados}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            {infoCliente.correosDestinatarios?.map((correo, index) => (
                              <Chip
                                key={index}
                                label={correo}
                                size="small"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" variant="body2">
                            Normativas Nuevas
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography 
                              variant="h4" 
                              color={infoCliente.cantidadNormativasNuevas > 0 ? "success.main" : "warning.main"}
                            >
                              {infoCliente.cantidadNormativasNuevas}
                            </Typography>
                            {infoCliente.cantidadNormativasNuevas > 0 ? (
                              <CheckCircleIcon color="success" />
                            ) : (
                              <WarningIcon color="warning" />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography color="text.secondary" variant="body2" gutterBottom>
                            Módulos Contratados
                          </Typography>
                          <Box>
                            {infoCliente.modulosContratados?.length > 0 ? (
                              infoCliente.modulosContratados.map((modulo, index) => (
                                <Chip
                                  key={index}
                                  label={modulo}
                                  size="small"
                                  color="secondary"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Sin módulos contratados
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Detalle de Normativas */}
                  {infoCliente.normativas?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        onClick={() => setMostrarDetallesNormativas(!mostrarDetallesNormativas)}
                        endIcon={mostrarDetallesNormativas ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        variant="text"
                        size="small"
                      >
                        {mostrarDetallesNormativas ? "Ocultar" : "Ver"} detalle de normativas ({infoCliente.normativas.length})
                      </Button>
                      
                      <Collapse in={mostrarDetallesNormativas}>
                        <List dense sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
                          {infoCliente.normativas.map((normativa, index) => (
                            <ListItem key={index} divider>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight="medium">
                                    {normativa.nombre}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {normativa.organismoEmisor} - {new Date(normativa.fechaPublicacion).toLocaleDateString()}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  )}

                  {/* Advertencia si no hay normativas */}
                  {infoCliente.cantidadNormativasNuevas === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <AlertTitle>Sin normativas pendientes</AlertTitle>
                      {infoCliente.puedeEnviarSinNormativas ? (
                        <Typography variant="body2">
                          No hay normativas nuevas, pero puede enviar un correo informativo activando la opción "Enviar sin normativas"
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          Este cliente (tipo Larrain Vial) no permite envío de correos sin normativas
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Paper>
              </Box>
            )}

            {/* Opciones de envío */}
            {infoCliente && (
              <Box sx={{ mb: 3 }}>
                {/* Switch para enviar sin normativas */}
                {infoCliente.puedeEnviarSinNormativas && infoCliente.cantidadNormativasNuevas === 0 && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enviarSinNormativas}
                        onChange={(e) => setEnviarSinNormativas(e.target.checked)}
                        disabled={enviando}
                      />
                    }
                    label="Enviar correo informativo sin normativas"
                  />
                )}

                {/* Mensaje personalizado */}
                <TextField
                  fullWidth
                  label="Mensaje Personalizado (opcional)"
                  multiline
                  rows={4}
                  value={mensajePersonalizado}
                  onChange={(e) => setMensajePersonalizado(e.target.value)}
                  disabled={enviando}
                  placeholder="Ingrese un mensaje personalizado que aparecerá al inicio del correo..."
                  helperText="Este mensaje se mostrará destacado al inicio del correo electrónico"
                  sx={{ mt: 2 }}
                />
              </Box>
            )}

            {/* Mensajes de error */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Resultado del envío */}
            {resultado && (
              <Alert 
                severity={resultado.exitoso ? "success" : "error"} 
                sx={{ mb: 2 }}
              >
                <AlertTitle>
                  {resultado.exitoso ? "¡Envío Exitoso!" : "Error en el Envío"}
                </AlertTitle>
                <Typography variant="body2">{resultado.mensaje}</Typography>
                {resultado.exitoso && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      Cliente: {resultado.clienteNombre}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Normativas: {resultado.cantidadNormativas}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Correos enviados: {resultado.correosEnviados}/{resultado.totalCorreos}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {/* Acciones */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                onClick={handleReset}
                disabled={enviando}
                variant="outlined"
              >
                Limpiar
              </Button>
              
              {infoCliente && (
                <Button
                  onClick={handleVerVistaPrevia}
                  disabled={enviando || !puedeEnviar()}
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                >
                  Vista Previa
                </Button>
              )}
              
              <Button
                onClick={handleEnviar}
                disabled={!puedeEnviar()}
                variant="contained"
                color="primary"
                startIcon={enviando ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {enviando ? "Enviando..." : "Enviar Correos"}
              </Button>
            </Box>
          </Paper>
        </Grid>
        {/* Modal de Vista Previa */}
        {mostrarVistaPrevia && infoCliente && (
          <VistaPreviaEnvioManualModal
            open={mostrarVistaPrevia}
            onClose={() => setMostrarVistaPrevia(false)}
            clienteId={parseInt(clienteSeleccionado)}
            clienteNombre={infoCliente.clienteNombre}
            mensajePersonalizado={mensajePersonalizado}
          />
        )}
      </Grid>
  );
};

export default EnvioManualCorreo;